import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "./api";
import useIdleLogout from "./useIdleLogout";
import {
  handleDownloadAneasthetistPDF,
} from "./pdfGenerators";

const copyToClipboard = (text) => {
  navigator.clipboard.writeText(text).then(() => {});
};



export default function PatientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [patient, setPatient] = useState(null);
  const { showWarning, stayLoggedIn } = useIdleLogout();

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletePassword, setDeletePassword] = useState("");
  const [deleteError, setDeleteError] = useState("");
  const [deleteSuccess, setDeleteSuccess] = useState("");

  const formatDateTime = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (isNaN(date)) return "";
    return `${date.getFullYear()}${String(date.getMonth() + 1).padStart(
      2,
      "0"
    )}${String(date.getDate()).padStart(2, "0")}${String(
      date.getHours()
    ).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
  };

  const formatDateOnly = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (isNaN(date)) return "";
    const yyyy = date.getFullYear();
    const mm = (date.getMonth() + 1).toString().padStart(2, "0");
    const dd = date.getDate().toString().padStart(2, "0");
    return `${yyyy}${mm}${dd}`;
  };

  const displayValue = (key, value) => {
    if (typeof value === "boolean") return value ? "Yes" : "No";
    if (key === "rec_cre_dte") return formatDateTime(value);
    if (key.includes("date_of_birth")) return formatDateOnly(value);
    return value || "";
  };

  useEffect(() => {
    const token = sessionStorage.getItem("token");
    if (!token) {
      navigate("/");
      return;
    }

    api
      .get(`patients/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        setPatient(res.data);
      })
      .catch((err) => {
        console.error(err);
        navigate("/patients");
      });
  }, [id, navigate]);

  if (!patient)
    return <div className="text-center mt-20 text-gray-600">Loading...</div>;

  const sectionFields = {
    "Patient Information": [
      "patient_identification_number",
      "patient_surname",
      "patient_full_names",
    ],
    Signature: ["signature_image"],
  };

  const formatLabel = (key) => {
    return key
      .replace(/^(main_|patient_|next_kin_)/, "") // Remove leading context
      .replace(/_/g, " ") // Convert underscores to spaces
      .replace(/\b\w/g, (l) => l.toUpperCase()); // Capitalize each word
  };

  return (
    <div
      className="min-h-screen w-full bg-cover bg-center text-gray-800"
      style={{
        backgroundImage: `radial-gradient(circle at top left, #A8A8A8, #8C8C8C, #85D3DE, #8DDFEB)`,
      }}
    >
      <div className="flex justify-center pt-6">
        <img
          src="/InfoMedi_logo.png"
          alt="InfoMedi Logo"
          className="h-20 drop-shadow"
        />
      </div>

      <div className="bg-white/30 backdrop-blur-md shadow-xl rounded-xl px-6 py-8 mt-8 max-w-7xl mx-auto border border-white/40">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <div className="flex flex-col gap-2">
            
            <button
              onClick={() => handleDownloadAneasthetistPDF(patient)}
              className="bg-gradient-to-r from-gray-400 to-gray-700 hover:from-gray-600 hover:to-gray-800 text-white py-2 rounded-lg shadow-md transition-all"
            >
              VIEW ANEASTHETIST FORM
            </button>
          </div>

          <div className="flex flex-col gap-2">
            
          </div>

          <div className="flex flex-col gap-2">
            <button
              onClick={() => navigate(`/patients/${id}/edit`)}
              className="bg-gradient-to-r from-gray-400 to-gray-700 hover:from-gray-600 hover:to-gray-800 text-white py-2 rounded-lg shadow-md transition-all"
            >
              EDIT
            </button>
            <button
              onClick={async () => {
                setDeletePassword("");
                setDeleteError("");
                setDeleteSuccess("");

                try {
                  const token = sessionStorage.getItem("token");
                  const res = await api.get(`patients/${id}`, {
                    headers: { Authorization: `Bearer ${token}` },
                  });
                  setPatient(res.data); // update state with fresh data
                } catch (err) {
                  console.error("Failed to refresh patient:", err);
                }

                setShowDeleteModal(true);
              }}
              className="bg-gradient-to-r from-gray-400 to-gray-700 hover:from-gray-600 hover:to-gray-800 text-white py-2 rounded-lg shadow-md transition-all"
            >
              DELETE
            </button>
            <button
              onClick={() => navigate("/patients")}
              className="bg-gradient-to-r from-gray-400 to-gray-700 hover:from-gray-600 hover:to-gray-800 text-white py-2 rounded-lg shadow-md transition-all"
            >
              BACK TO LIST
            </button>
            <button
              onClick={() => {
                sessionStorage.removeItem("token");
                navigate("/");
              }}
              className="bg-gradient-to-r from-gray-400 to-gray-700 hover:from-gray-600 hover:to-gray-800 text-white py-2 rounded-lg shadow-md transition-all"
            >
              LOGOUT
            </button>
          </div>
        </div>

        <div className="mb-6">
          <div className="grid sm:grid-cols-2 gap-x-12 text-sm text-gray-800">
          <div>
          <strong className="text-blue-700">Request Number:</strong>{" "}
          {patient.request_number}

          <span className="ml-6">
            <strong className="text-blue-700">Follow-up?</strong>{" "}
            {patient.follow_up ? "Yes" : "No"}
          </span>
        </div>
          </div>
        </div>

        {Object.entries(sectionFields).map(([sectionTitle, keys]) => {
          const half = Math.ceil(keys.length / 2);
          const col1 = keys.slice(0, half);
          const col2 = keys.slice(half);
          return (
            <div key={sectionTitle} className="mb-6">
              <h2 className="text-xl font-bold text-blue-700 border-b border-blue-200 mb-4 pb-1">
                {sectionTitle}
              </h2>
              <div className="grid sm:grid-cols-2 gap-x-12 text-sm text-gray-800">
                <div className="flex flex-col gap-2">
                  {col1.map((key) =>
                    key === "signature_image" && patient[key] ? (
                      <div key={key}>
                        <strong className="text-blue-700">
                          {formatLabel(key)}:
                        </strong>
                        <br />
                        <img
                          src={`data:image/png;base64,${patient[key]}`}
                          alt="Signature"
                          className="border rounded mt-2 max-w-full"
                        />
                      </div>
                    ) : (
                      <div key={key}>
                        <strong className="text-blue-700">
                          {formatLabel(key)}:
                        </strong>{" "}
                        {displayValue(key, patient[key])}
                        {key !== "request_number" &&
                          key !== "signature_image" && (
                            <button
                              onClick={() =>
                                copyToClipboard(displayValue(key, patient[key]))
                              }
                              className="ml-2 text-xs text-black-500 border border-blue-300 px-1 py-0.5 rounded hover:bg-blue-100"
                              title="Copy to clipboard"
                            >
                              Copy
                            </button>
                          )}
                      </div>
                    )
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  {col2.map((key) =>
                    key === "signature_image" && patient[key] ? (
                      <div key={key}>
                        <strong className="text-blue-700">
                          {formatLabel(key)}:
                        </strong>
                        <br />
                        <img
                          src={`data:image/png;base64,${patient[key]}`}
                          alt="Signature"
                          className="border rounded mt-2 max-w-full"
                        />
                      </div>
                    ) : (
                      <div key={key}>
                        <strong className="text-blue-700">
                          {formatLabel(key)}:
                        </strong>{" "}
                        {displayValue(key, patient[key])}
                        {key !== "request_number" && (
                          <button
                            onClick={() =>
                              copyToClipboard(displayValue(key, patient[key]))
                            }
                            className="ml-2 text-xs text-black-500 border border-blue-300 px-1 py-0.5 rounded hover:bg-blue-100"
                            title="Copy to clipboard"
                          >
                            Copy
                          </button>
                        )}
                      </div>
                    )
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-sm shadow-xl">
            <h2 className="text-xl font-semibold mb-4 text-gray-800">
              Confirm Deletion
            </h2>
            <p className="text-sm text-gray-600 mb-4">
              Please enter your password to confirm deletion of this patient.
            </p>
            <input
              type="password"
              placeholder="Enter your password"
              className="w-full p-2 border border-gray-300 rounded mb-3"
              value={deletePassword}
              onChange={(e) => setDeletePassword(e.target.value)}
            />
            {deleteError && (
              <p className="text-red-600 text-sm mb-2">{deleteError}</p>
            )}
            {deleteSuccess && (
              <p className="text-green-700 text-sm mb-2">{deleteSuccess}</p>
            )}
            <div className="flex justify-between">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 rounded bg-gray-300 hover:bg-gray-400"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  setDeleteError("");

                  const storedPassword = sessionStorage.getItem("password");
                  if (deletePassword !== storedPassword) {
                    setDeleteError("Incorrect password.");
                    return;
                  }

                 

                  try {
                    const token = sessionStorage.getItem("token");
                    await api.post(
                      `/patients/${id}/delete`,
                      {
                        username: sessionStorage.getItem("username"),
                        password: deletePassword,
                      },
                      {
                        headers: { Authorization: `Bearer ${token}` },
                      }
                    );

                    setDeleteSuccess("Patient successfully deleted.");
                    setTimeout(() => navigate("/patients"), 1500);
                  } catch (err) {
                    setDeleteError("Deletion failed. Try again.");
                  }
                }}
                className="px-4 py-2 rounded bg-red-600 text-white hover:bg-red-700"
              >
                Confirm Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {showWarning && (
        <div className="fixed bottom-4 right-4 bg-yellow-100 text-yellow-900 border border-yellow-400 px-4 py-2 rounded shadow-lg z-50">
          <p>You will be logged out due to inactivity in 30 seconds.</p>
          <button
            onClick={stayLoggedIn}
            className="mt-2 px-3 py-1 bg-blue-600 text-white rounded"
          >
            Stay Logged In
          </button>
        </div>
      )}
    </div>
  );
}
